import { msg } from "../../locales/translate.ts";
export interface RollingPeriodTotal {
	currency: string;
	taken: number;
	released: number;
	taken_count: number;
	released_count: number;
}

interface RollingPeriodEntity {
	id: number;
	name: string;
	currencies: RollingPeriodTotal[];
}

export interface RollingPeriodReport {
	totals: RollingPeriodTotal[];
	clients: (RollingPeriodEntity & { mids: RollingPeriodEntity[] })[];
	coverage: { oldest: string | null; newest: string | null; partial: boolean };
	notes: string[];
}

const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const amount = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
const count = (value: unknown): value is number => amount(value) && Number.isSafeInteger(value) && value >= 0;
const date = (value: unknown): value is string | null => value === null || typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

const totals = (value: unknown): RollingPeriodTotal[] => {
	if (!Array.isArray(value)) throw new Error(msg("missingRollingPeriodTotals"));
	const currencies = new Set<string>();
	return value.map(row => {
		if (!record(row) || typeof row.currency !== "string" || !/^[A-Z]{3}$/.test(row.currency) || currencies.has(row.currency)
			|| !amount(row.taken) || !amount(row.released) || !count(row.taken_count) || !count(row.released_count)) throw new Error(msg("invalidRollingPeriodTotals"));
		currencies.add(row.currency);
		return { currency: row.currency, taken: row.taken, released: row.released, taken_count: row.taken_count, released_count: row.released_count };
	});
};

const entity = (value: unknown): RollingPeriodEntity => {
	if (!record(value) || !count(value.id) || typeof value.name !== "string") throw new Error(msg("invalidRollingPeriodEntity"));
	return { id: value.id, name: value.name, currencies: totals(value.currencies) };
};

export const parseRollingPeriodReport = (data: unknown, from: string, to: string): RollingPeriodReport => {
	if (!record(data) || !record(data.period) || data.period.from !== from || data.period.to !== to
		|| !record(data.coverage) || typeof data.coverage.partial !== "boolean" || !date(data.coverage.oldest) || !date(data.coverage.newest)
		|| !Array.isArray(data.clients) || !Array.isArray(data.notes) || !data.notes.every(note => typeof note === "string")) throw new Error(msg("unexpectedRollingPeriodReport"));
	return {
		totals: totals(data.totals),
		clients: data.clients.map(client => {
			if (!record(client) || !Array.isArray(client.mids)) throw new Error(msg("invalidRollingPeriodClient"));
			return { ...entity(client), mids: client.mids.map(entity) };
		}),
		coverage: { oldest: data.coverage.oldest, newest: data.coverage.newest, partial: data.coverage.partial },
		notes: data.notes,
	};
};
